// hooks/useUsers.js  — shared hook used by all user-role pages
import { useState, useEffect, useCallback } from 'react';
import { userController } from '../controllers/userController.js';

export function useUsers({ role, initialFilters = {} } = {}) {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [total, setTotal]             = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [page, setPage]               = useState(1);
  const [filters, setFilters]         = useState({ role: role || 'All', status: 'All', search: '', ...initialFilters });
  const [sort, setSort]               = useState({ field: 'createdAt', dir: 'desc' });
  const [selected, setSelected]       = useState([]);
  const limit = 10;

  const fetch = useCallback(async () => {
    setLoading(true);
    const result = role
      ? await userController.getByRole(role, { filters, sort, page, limit })
      : await userController.getAll({ filters, sort, page, limit });
    if (result.success) {
      setUsers(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    }
    setLoading(false);
  }, [role, filters, sort, page]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSort = (field) => {
    setSort(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const handleFilter = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
    setPage(1);
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll    = () => setSelected(prev => prev.length === users.length ? [] : users.map(u => u.id));

  return {
    users, loading, total, totalPages, page, setPage,
    filters, handleFilter, sort, handleSort,
    selected, toggleSelect, toggleAll, setSelected,
    refresh: fetch,
  };
}

// ── Shared UI primitives ───────────────────────────────────────────────────
export const C = {
  // Layout
  page:    { padding: '0' },
  header:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px', flexWrap:'wrap', gap:'16px' },
  title:   { fontSize:'26px', fontWeight:'700', color:'#0f172a', margin:0 },
  // Buttons
  btn:     { display:'flex', alignItems:'center', gap:'8px', padding:'9px 18px', border:'none', borderRadius:'8px', fontSize:'14px', fontWeight:'500', cursor:'pointer', transition:'opacity .15s' },
  btnPrim: { backgroundColor:'#3b82f6', color:'#fff' },
  btnSec:  { backgroundColor:'#fff', color:'#1e293b', border:'1px solid #e2e8f0' },
  btnDanger:{ backgroundColor:'#fee2e2', color:'#dc2626', border:'1px solid #fecaca' },
  btnGreen: { backgroundColor:'#10b981', color:'#fff' },
  // Stats
  statsBar: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'14px', marginBottom:'22px' },
  statCard: { backgroundColor:'#fff', padding:'18px 20px', borderRadius:'12px', boxShadow:'0 1px 3px rgba(0,0,0,.08)', borderLeft:'4px solid transparent' },
  statLabel:{ fontSize:'13px', color:'#64748b', marginBottom:'6px' },
  statVal:  { fontSize:'26px', fontWeight:'700', color:'#0f172a' },
  // Filter bar
  filterBar:{ display:'flex', gap:'10px', marginBottom:'18px', flexWrap:'wrap', alignItems:'center' },
  searchWrap:{ flex:1, minWidth:'220px', position:'relative' },
  searchIn:  { width:'100%', padding:'9px 9px 9px 38px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'14px', outline:'none', boxSizing:'border-box' },
  searchIco: { position:'absolute', left:'11px', top:'50%', transform:'translateY(-50%)', color:'#94a3b8' },
  select:    { padding:'9px 14px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'14px', outline:'none', backgroundColor:'#fff', cursor:'pointer' },
  // Table
  card:    { backgroundColor:'#fff', borderRadius:'12px', boxShadow:'0 1px 3px rgba(0,0,0,.08)', overflow:'hidden' },
  table:   { width:'100%', borderCollapse:'collapse' },
  th:      { backgroundColor:'#f8fafc', padding:'13px 16px', textAlign:'left', fontSize:'12px', fontWeight:'600', color:'#475569', borderBottom:'1px solid #e2e8f0', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' },
  td:      { padding:'13px 16px', borderBottom:'1px solid #f1f5f9', fontSize:'13px', color:'#1e293b', verticalAlign:'middle' },
  // Badges
  badge:   { display:'inline-block', padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'600' },
  // Actions
  actBtn:  { padding:'5px 7px', backgroundColor:'transparent', border:'none', borderRadius:'6px', cursor:'pointer', display:'inline-flex', alignItems:'center' },
  // Pagination
  pagi:    { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderTop:'1px solid #e2e8f0' },
  // Modal
  overlay: { position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal:   { backgroundColor:'#fff', borderRadius:'14px', width:'92%', maxWidth:'680px', maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.2)' },
  mHead:   { padding:'18px 24px', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' },
  mTitle:  { fontSize:'18px', fontWeight:'600', color:'#0f172a', margin:0 },
  mBody:   { padding:'24px' },
  mFoot:   { padding:'14px 24px', borderTop:'1px solid #e2e8f0', display:'flex', justifyContent:'flex-end', gap:'10px' },
  formGroup:{ marginBottom:'18px' },
  label:   { display:'block', marginBottom:'6px', fontSize:'13px', fontWeight:'500', color:'#374151' },
  input:   { width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'14px', outline:'none', boxSizing:'border-box' },
  errBox:  { backgroundColor:'#fee2e2', border:'1px solid #fecaca', borderRadius:'8px', padding:'10px 14px', marginBottom:'16px', fontSize:'13px', color:'#dc2626' },
};